import {Container, Typography} from '@mui/material';
import React, {useCallback, useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {Roles} from '../enums';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {
  getPlayerSessionsAsync,
  setPlayerSessionFilter,
} from '../store/playerSessionSlice';
import FamilyMemberList from './shared/FamilyMemberList';
import PageTitle from './shared/PageTitle';
import PlayerSessionsList from './shared/PlayerSessionsList';
import TeamList from './shared/TeamList';
import {Navigate} from 'react-router-dom';
import PlayerSessionFilter, {
  PlayerSessionFilterForm,
} from './shared/PlayerSessionFilter';
import PageTitleContainer from './shared/PageTitleContainer';
import FullScreenLoadingSpinner from './shared/FullScreenLoading';
import {
  filterPlayerSessions,
  getUniquePlayerSessionTeams,
} from '../services/playerSessionService';

interface Props {}

const Dashboard: React.FC<Props> = () => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const role = useAppSelector(state => state?.profile?.profile?.role);
  const profileId = useAppSelector(state => state?.profile?.profile?.id);
  const filter = useAppSelector(
    state => state.playerSession.playerSessionFilter,
  );
  const playerSessions = useAppSelector(
    state => state?.playerSession?.playerSessions,
  );
  const playerSessionsLoading = useAppSelector(
    state => state.playerSession.status === 'loading',
  );
  const teams = useAppSelector(state => state?.team?.teams);
  const families = useAppSelector(state => state?.family?.families);

  const onSessionFilterUpdate = useCallback(
    (filterData: PlayerSessionFilterForm) => {
      dispatch(setPlayerSessionFilter(filterData));
    },
    [dispatch],
  );

  const filteredPlayerSessions = useMemo(() => {
    if (!filter) {
      return playerSessions;
    }
    return filterPlayerSessions(playerSessions ?? [], filter);
  }, [filter, playerSessions]);

  const playerSessionTeams = useMemo(() => {
    return getUniquePlayerSessionTeams(playerSessions ?? []);
  }, [playerSessions]);

  useEffect(() => {
    if ([Roles.player, Roles.pro].indexOf(role) > -1 && profileId) {
      dispatch(
        getPlayerSessionsAsync({
          profileId,
        }),
      );
    } else {
      dispatch(getPlayerSessionsAsync({}));
    }
  }, [profileId, dispatch, role]);

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {pageName: t('pages.dashboard')})}
        </title>
      </Helmet>
      <Container>
        {[Roles.player, Roles.pro].indexOf(role) > -1 && (
          <>
            <PageTitleContainer>
              <PageTitle noBottomMargin>
                {t('general.recentPlayerSessions')}
              </PageTitle>
              <PlayerSessionFilter
                onFilterUpdate={onSessionFilterUpdate}
                currentFilters={filter}
                teams={playerSessionTeams ?? []}
              />
            </PageTitleContainer>
            {[...(filteredPlayerSessions ?? [])].length > 0 ? (
              <PlayerSessionsList
                navigateUrlPrefix="/playersessions"
                playerSessions={[...(filteredPlayerSessions ?? [])]}
                showDate={true}
              />
            ) : (
              <>
                {playerSessionsLoading ? (
                  <FullScreenLoadingSpinner />
                ) : (
                  <Typography variant="h3" sx={{textAlign: 'center'}}>
                    {t('sessionFilter.noSessions')}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
        {role === Roles.coach && (
          <>
            {(teams ?? []).length > 0 ? (
              <Navigate to={`/teams/${teams[0].id}/players`} />
            ) : (
              <>
                <PageTitle>{t('pages.teams')}</PageTitle>
                <TeamList teams={[...(teams ?? [])]} />
              </>
            )}
          </>
        )}
        {role === Roles.parent && (
          <>
            <PageTitle>{t('general.familyMembers')}</PageTitle>
            {(families ?? []).length > 0 && families[0].childProfiles ? (
              <FamilyMemberList
                familyMembers={[...(families?.[0]?.childProfiles ?? [])]}
                showPlayerSessionsLink={true}
                showMembershipLink={true}
              />
            ) : (
              <Typography variant="body1">
                {t('general.noFamilyConfigured')}
              </Typography>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default Dashboard;
